// Faktura til en kundeliste eller et abonnement blir en faktura per kunde: egen
// kunde-id, eget fakturanummer og egen kundefordring. Tidligere ble det bokfort en
// kundefordring paa en kunde som ikke fantes (listens navn), og kunder uten
// e-post fikk aldri noen faktura. Kunder uten e-post faar fakturaen opprettet;
// brukeren faar vite at den maa sendes eller skrives ut manuelt.

const customerListInvoiceIds = (source, fallback = null) => Array.from(new Set(
  safeArray(source?.customerIds || fallback?.customerIds || source?.customer?.customerIds || fallback?.customer?.customerIds)
    .map((id) => String(id || '').trim())
    .filter(Boolean),
));

const customerListMemberName = (customer, customerId) => customer?.name || customerId;

const createCustomerListInvoices = async (listInvoice, fallback = null) => {
  const customerIds = customerListInvoiceIds(listInvoice, fallback);
  if (!customerIds.length) throw new Error(t('sale.list.noMembers', 'Listen har ingen kunder å fakturere.'));
  const state = mergeState(runtime.appState);
  const customersById = new Map(safeArray(state.customers).map((customer) => [String(customer.id || ''), customer]));
  const invoices = [];
  const missingEmail = [];
  const failed = [];
  for (const customerId of customerIds) {
    const customer = customersById.get(customerId);
    if (!customer || customer.status === 'archived') {
      failed.push({ customerId, reason: t('sale.list.customerMissing', 'Kunden finnes ikke lenger.') });
      continue;
    }
    // Den forste fakturaen overtar id-en til listeutkastet, slik at utkastet ikke
    // blir liggende igjen ved siden av fakturaene som ble laget av det.
    const reuseSource = invoices.length === 0;
    const perCustomerInvoice = stripUndefinedDeep({
      ...listInvoice,
      id: reuseSource ? listInvoice.id : `inv${Date.now()}-${invoices.length}-${Math.random().toString(36).slice(2, 7)}`,
      invoiceNumber: reuseSource ? (listInvoice.invoiceNumber || '') : '',
      customer: invoiceCustomerFromBackendCustomer(customer),
      customerId,
      customerIds: undefined,
      customerListBatch: undefined,
      customerListNotice: undefined,
      status: 'completed',
      invoiceType: null,
      sentAt: undefined,
      sendEvents: reuseSource ? safeArray(listInvoice.sendEvents) : [],
      pdfStoragePath: undefined,
      pdfDownloadUrl: undefined,
      pdfFileName: undefined,
      pdfMimeType: undefined,
      pdfGeneratedAt: undefined,
      pdf: undefined,
      updatedAt: nowIso(),
    });
    try {
      const saved = await upsertInvoice(perCustomerInvoice);
      invoices.push(saved);
      if (!String(customer.email || '').trim()) {
        missingEmail.push({ customerId, name: customerListMemberName(customer, customerId), invoiceId: saved.id, invoiceNumber: saved.invoiceNumber || '' });
      }
    } catch (error) {
      failed.push({ customerId, name: customerListMemberName(customer, customerId), reason: error?.message || t('sale.create.createFailed', 'Kunne ikke opprette salget.') });
    }
  }
  if (!invoices.length) throw new Error(failed[0]?.reason || t('sale.list.noneCreated', 'Ingen fakturaer ble opprettet.'));
  return { invoices, missingEmail, failed };
};

// «Opprett» fra salgseditoren. Returnerer en samlet handle som dialogen kan vise
// og sende videre; sendingen bruker fakturaene som allerede er opprettet.
const createCustomerListInvoicesFromEditor = async (listInvoice) => {
  const batch = await createCustomerListInvoices(listInvoice);
  const recurringFailed = [];
  if (listInvoice.recurring?.enabled && typeof saveRecurringInvoiceAgreement === 'function') {
    for (const invoice of batch.invoices) {
      try {
        await saveRecurringInvoiceAgreement(invoice);
      } catch (error) {
        recurringFailed.push({ customerId: invoice.customerId, name: invoice.customer?.name, reason: error?.message || t('sale.create.recurringSaveFailed') });
      }
    }
  }
  const numbers = batch.invoices.map((invoice) => invoice.invoiceNumber).filter(Boolean);
  const notices = [
    batch.missingEmail.length ? t('sale.list.missingEmailNotice', '{count} fakturaer er opprettet for kunder uten e-post og må sendes eller skrives ut manuelt: {names}', { count: batch.missingEmail.length, names: batch.missingEmail.map((entry) => entry.name).join(', ') }) : '',
    [...batch.failed, ...recurringFailed].length ? t('sale.list.notCreatedNotice', 'Ikke fullført for: {names}', { names: [...batch.failed, ...recurringFailed].map((entry) => `${entry.name || entry.customerId} (${entry.reason})`).join(', ') }) : '',
  ].filter(Boolean);
  const first = batch.invoices[0];
  return {
    ...first,
    recurring: undefined,
    invoiceNumber: numbers.length > 1 ? `${numbers[0]}–${numbers[numbers.length - 1]}` : (numbers[0] || ''),
    customer: { ...(first.customer || {}), name: listInvoice.customer?.name || first.customer?.name, isList: true },
    customerIds: batch.invoices.map((invoice) => invoice.customerId),
    customerListBatch: batch,
    customerListNotice: notices.join(' ') || undefined,
  };
};

window.PlummeCustomerListInvoices = { create: createCustomerListInvoicesFromEditor };
